import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { ChartOptions } from 'chart.js';
import CustomLegendES from './CustomLegendES';
import YoYGrowthBarChart from './YoYGrowthBarChart';
import { downloadXLS } from 'utils/download';
import TooltipPopover from 'components/TooltipPopover';
import DownloadMenu from 'components/DownloadMenu';
import { noDataPlugin } from 'utils';

interface YoYChangeProps {
  data: {
    data: Array<{
      date_formatted: string;
      Feature_Group: string;
      total_demand: number;
    }>;
    growth_percentage_per_feature_group: Array<{
      Feature_Group: string;
      demand_previous_year: number;
      demand_current_year: number;
      growth_percentage: number;
    }>;
  };
}

const YoYChange: React.FC<YoYChangeProps> = ({
  data = { data: [], growth_percentage_per_feature_group: [] },
}) => {
  const [visibility, setVisibility] = useState<Record<string, boolean>>({});
  const [isLegendExpanded, setIsLegendExpanded] = useState(false);

  const colors = [
    'rgba(34, 34, 34, 0.80)',
    'rgba(89, 89, 89, 0.8)',
    'rgba(144, 144, 144, 0.8)',
    'rgba(50, 215, 111, 0.8)',
    'rgba(3, 197, 255, 0.8)',
    'rgba(84, 79, 197, 0.8)',
    'rgba(254, 106, 53, 0.8)',
    'rgba(107, 138, 188, 0.8)',
    'rgba(213, 104, 251, 0.8)',
    'rgba(47, 224, 202, 0.8)',
    'rgba(250, 75, 66, 0.8)',
    'rgba(254, 181, 106, 0.8)',
    'rgba(254, 181, 106, 0.8)',
  ];

  const normalizeLabel = (label: string) => label.replace(/ /g, '_');

  useEffect(() => {
    const initialVisibility = data.data.reduce((acc, item) => {
      const normalizedFeatureGroup = normalizeLabel(item.Feature_Group);
      acc[normalizedFeatureGroup] = normalizedFeatureGroup !== 'uncategorized';
      return acc;
    }, {} as Record<string, boolean>);

    setVisibility(initialVisibility);
  }, [data]);

  const formatChartData = (data: YoYChangeProps['data']['data']) => {
    if (!Array.isArray(data)) return { labels: [], datasets: [] };
    const labels = Array.from(new Set(data.map((item) => item.date_formatted)));
    const featureGroups = Array.from(
      new Set(data.map((item) => item.Feature_Group))
    );

    const datasets = featureGroups.map((featureGroup, index) => {
      const normalizedFeatureGroup = normalizeLabel(featureGroup);
      const featureData = data.filter(
        (item) => item.Feature_Group === featureGroup
      );
      const totalDemands = labels.map((label) => {
        const item = featureData.find(
          (dataItem) => dataItem.date_formatted === label
        );
        return item ? item.total_demand : 0;
      });

      const color = colors[index % colors.length];
      const fadedColor = color.replace(
        /rgba?\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/,
        (match, p1, p2, p3, p4) => {
          const alpha = parseFloat(p4) * 0.2;
          return `rgba(${p1}, ${p2}, ${p3}, ${alpha})`;
        }
      );

      return {
        label: featureGroup,
        data: totalDemands,
        borderColor: visibility[normalizedFeatureGroup] ? color : fadedColor,
        borderWidth: 1.5,
        fill: false,
        pointRadius: 0,
        tension: 0.4,
        hidden: !visibility[normalizedFeatureGroup],
        color: color,
      };
    });

    return { labels, datasets };
  };

  const chartData = formatChartData(data.data);

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Month',
          color: '#4D4D4D',
          font: {
            size: 14,
            style: 'normal',
            weight: 700,
            lineHeight: 1.28,
            family: 'DM Sans',
          },
        },
        ticks: {
          color: '#333',
          font: {
            size: 14,
            style: 'normal',
            weight: 500,
            lineHeight: 1.28,
            family: 'DM Sans',
          },
          maxRotation: 0,
          minRotation: 0,
        },
        grid: {
          color: '#B3B3B3',
        },
        border: {
          color: '#333333',
          display: true,
        },
      },
      y: {
        title: {
          display: true,
          text: 'Demand',
          color: '#4D4D4D',
          font: {
            size: 14,
            style: 'normal',
            weight: 700,
            lineHeight: 1.28,
            family: 'DM Sans',
          },
        },
        ticks: {
          display: true,
          color: '#333',
          font: {
            size: 14,
            style: 'normal',
            weight: 500,
            lineHeight: 1.28,
            family: 'DM Sans',
          },
          align: 'center',
          callback: function (value) {
            const numericValue = Number(value);
            return numericValue >= 1000000
              ? numericValue / 1000000 + 'M'
              : numericValue >= 1000
              ? numericValue / 1000 + 'K'
              : numericValue;
          },
        },
        grid: {
          color: '#B3B3B3',
        },
        border: {
          color: '#333333',
          display: true,
        },
      },
    },
  };

  const handleLegendClick = (label: string) => {
    const normalizedLabel = normalizeLabel(label);
    setVisibility((prevVisibility) => ({
      ...prevVisibility,
      [normalizedLabel]: !prevVisibility[normalizedLabel],
    }));
  };

  const legends = chartData.datasets.map((dataset) => ({
    label: dataset.label,
    color: dataset.borderColor,
    visibility: visibility[normalizeLabel(dataset.label)],
  }));

  const visibleLegends = isLegendExpanded ? legends : legends.slice(0, 8);

  // XLS file download
  const downloadChartData = () => {
    const headers = [
      'Month',
      ...chartData.datasets.map((dataset) => dataset.label),
    ];
    const labels = chartData.labels;
    const columns = chartData.datasets.map((dataset) => dataset.data);

    const growthHeaders = ['Feature Group', 'Growth Percentage'];
    const growthLabels = data.growth_percentage_per_feature_group.map(
      (item) => item.Feature_Group
    );
    const growthColumns = data.growth_percentage_per_feature_group.map(
      (item) => item.growth_percentage
    );

    downloadXLS('YoY_Change_Feature_Group_Data', headers, labels, columns);

    downloadXLS('YoY_Growth_Feature_Group_Data', growthHeaders, growthLabels, [
      growthColumns,
    ]);
  };

  return (
    <div id="YOY_Change_FG" className="bg-light rounded-2xl shadow-sm mt-8">
      <div className="flex justify-between items-center border-b border-success mb-2">
        <div>
          <TooltipPopover
            content="consumer_intent_page.product_and_feature_analysis_tab.yoy_change_in_demand_by_feature_group_percentage"
            type="info"
            className="top-2 right-[-1rem]"
            side="top"
          >
            <h2 className="text-primarygray text-2xl font-bold mb-1 pl-4 pt-2">
              YoY Change in Demand by Feature Group (%)
            </h2>
          </TooltipPopover>
          <h3 className="text-primarygray text-sm font-bold mb-2 pl-4">
            How consumer searches evolve per feature group
          </h3>
        </div>
        <DownloadMenu
          id="downloadDropdownMenu-YOY_Change_FG"
          onDownloadXLS={() => downloadChartData()}
          imageParam="YOY_Change_FG"
        />
      </div>
      <div className="flex relative">
        <div className="flex relative pl-2 py-6 justify-center items-center md:h-[295px] 2xl:w-[85%] xl:w-[85%] md:w-[85%] w-[85%]">
          <Line data={chartData} options={options} plugins={[noDataPlugin]} />
        </div>
        <div className="flex-none text-xs flex flex-col items-start pl-4 pr-4 py-6 2xl:w-[15%] xl:w-[15%] md:w-[15%] w-[15%]">
          <CustomLegendES
            brandsData={visibleLegends}
            onLegendClick={handleLegendClick}
            visibility={visibility}
          />
          <button
            onClick={() => setIsLegendExpanded(!isLegendExpanded)}
            className="mt-2 bg-light hover:underline"
          >
            {isLegendExpanded ? 'Show Less' : 'Show More'}
          </button>
        </div>
      </div>
      <YoYGrowthBarChart
        growthPercentageData={data.growth_percentage_per_feature_group}
      />
    </div>
  );
};

export default YoYChange;
